// LOHR footer signup. Public site ID only; never put a private key in this asset.
export function normalizedPhone(input) {
  const raw = String(input || '').trim();
  if (!raw) return undefined;
  const compact = raw.replace(/[\s().-]/g, '');
  if (/^0[2-478]\d{8}$/.test(compact)) return '+61' + compact.slice(1);
  if (/^61[2-478]\d{8}$/.test(compact)) return '+' + compact;
  if (/^\+[1-9]\d{7,14}$/.test(compact)) return compact;
  throw new Error('Please enter a valid phone number, including the country code if outside Australia.');
}

export function subscriptionPayload(values, pathname = '/') {
  const firstName = String(values.firstName || '').trim();
  const lastName = String(values.lastName || '').trim();
  const email = String(values.email || '').trim().toLowerCase();
  if (!firstName || !lastName) throw new Error('Please enter your first and last name.');
  if (firstName.length > 100 || lastName.length > 100) throw new Error('Please keep each name under 100 characters.');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 254) throw new Error('Please enter a valid email address.');
  const phone = normalizedPhone(values.phone);
  return {data:{type:'subscription',attributes:{
    custom_source:'LOHR website footer',
    profile:{data:{type:'profile',attributes:{
      email, first_name:firstName, last_name:lastName,
      ...(phone ? {phone_number:phone} : {}),
      // Explicit email-only object is essential: a phone number is NOT SMS consent.
      subscriptions:{email:{marketing:{consent:'SUBSCRIBED'}}},
      properties:{signup_source:'lohr-footer',signup_page_path:String(pathname).split(/[?#]/)[0].slice(0,500)}
    }}}
  },relationships:{list:{data:{type:'list',id:'TQHkYf'}}}}};
}

export function bindFooterSignup(form, {fetchFn=globalThis.fetch, pathname=globalThis.location?.pathname || '/', timeoutMs=15000}={}) {
  if (form.dataset.lohrBound) return;
  form.dataset.lohrBound='true';
  const button=form.querySelector('[data-submit]');
  const message=form.querySelector('[data-message]');
  let pending=false, accepted=false;
  const say=(text,error=false)=>{
    message.textContent=text; message.hidden=false;
    message.setAttribute('role',error?'alert':'status');
    message.focus();
  };
  button.disabled=false;
  form.addEventListener('submit',async event=>{
    event.preventDefault();
    if (pending || accepted) return;
    if (form.elements.website.value) return;
    if (!form.checkValidity()) {form.reportValidity();return;}
    let payload;
    try {payload=subscriptionPayload({firstName:form.elements.first_name.value,lastName:form.elements.last_name.value,email:form.elements.email.value,phone:form.elements.phone.value},pathname);}
    catch(error){say(error.message,true);return;}
    pending=true;button.disabled=true;message.hidden=true;
    const controller=new AbortController();
    const timeout=setTimeout(()=>controller.abort(),timeoutMs);
    let httpStatus=0;
    try {
      const response=await fetchFn('https://a.klaviyo.com/client/subscriptions/?company_id=WDMY98',{
        method:'POST',headers:{'Content-Type':'application/vnd.api+json',revision:'2026-07-15'},
        body:JSON.stringify(payload),signal:controller.signal
      });
      httpStatus=response.status;
      if (response.status!==202) throw new Error('subscription_rejected');
      accepted=true;
      // A 202 can accept email but reject phone. Do not invite a second subscription
      // request: retain email acceptance and explain the unsaved optional field.
      // This header may not be exposed by CORS, so provider read-back is still QA.
      const partialFailure=response.headers?.get?.('X-Klaviyo-Partial-Failure') || '';
      const phoneNotSaved=partialFailure.toLowerCase().split(/[\s,]+/).includes('phone_number');
      if (phoneNotSaved) {
        say('Email subscription request received, but your phone number could not be saved. If you are not already subscribed, check your inbox to confirm your email subscription. To update your phone number, contact hello@lohrjewellery.com.au.',true);
      } else {
        say('Request received. If you are not already subscribed, check your inbox to confirm your email subscription.');
      }
    } catch {
      try { globalThis.LohrSignupError?.('footer','subscriptions',httpStatus); } catch {}
      say('We could not confirm your subscription request. Please try again, or contact hello@lohrjewellery.com.au.',true);
    } finally {clearTimeout(timeout);pending=false;button.disabled=accepted;}
  });
}

// The footer keeps only a link; the fields open in a modal. Native showModal()
// is what puts the panel in the top layer, clear of the footer's clip-path.
export function bindSignupDialog(root, {doc=globalThis.document}={}) {
  const dialog=root.querySelector('[data-signup-dialog]');
  const openButton=root.querySelector('[data-signup-open]');
  if (!dialog || !openButton || root.dataset.lohrDialogBound) return;
  if (typeof dialog.showModal !== 'function') {
    // No dialog support: send them somewhere they can still reach us.
    openButton.hidden=true;
    return;
  }
  root.dataset.lohrDialogBound='true';

  openButton.addEventListener('click',()=>{
    if (dialog.open) return;
    dialog.showModal();
    // Lenis keeps scrolling the page behind a modal, so stop it as well.
    doc.documentElement.setAttribute('scroll-lock','');
    globalThis.lenis?.stop?.();
  });

  // `close` covers the close button, Escape and any programmatic close.
  dialog.addEventListener('close',()=>{
    doc.documentElement.removeAttribute('scroll-lock');
    globalThis.lenis?.start?.();
  });

  root.querySelectorAll('[data-signup-close]').forEach(button=>{
    button.addEventListener('click',()=>dialog.close());
  });

  dialog.addEventListener('click',event=>{
    // Only a click on the dialog box itself can be a backdrop click. Without
    // this guard, Enter on the Subscribe button (clientX/clientY of 0) reads as
    // a click outside and closes the form mid-submit.
    if (event.target !== dialog) return;
    const rect=dialog.getBoundingClientRect();
    const inside=event.clientX>=rect.left && event.clientX<=rect.right
      && event.clientY>=rect.top && event.clientY<=rect.bottom;
    if (!inside) dialog.close();
  });
}

if (typeof document !== 'undefined') {
  const init=scope=>{
    scope.querySelectorAll('[data-lohr-signup]').forEach(root=>bindSignupDialog(root));
    scope.querySelectorAll('[data-lohr-footer-signup]').forEach(form=>bindFooterSignup(form));
  };
  init(document);
  document.addEventListener('shopify:section:load',event=>init(event.target));
}
